import {
  NativeStackHeaderProps,
  Stack,
  useIsFocused,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useAccountStatements from 'features/myVaro/hooks/statementsAndForms/useAccountStatements';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import SectionedContentScreen, {
  SectionedContentScreenModel,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import {
  showZendeskSection,
  ZendeskSections,
} from 'shared/utils/zendesk/zendeskUtils';

const PAY_OVER_TIME_ACCOUNT_NAME = 'Pay Over Time Account';

const AccountSettings = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'accountSettings',
  });

  const { bottom } = useSafeAreaInsets();
  const isFocused = useIsFocused();

  const { accountId = '' } = useLocalSearchParams<{ accountId: string }>();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/pay-over-time/account-settings',
  });

  const { accountStatementsDetails = [] } = useAccountStatements({
    accountId: '',
    pause: !isFocused,
  });

  const payOverTimeAccount = accountStatementsDetails?.find(
    (account) => account?.name === PAY_OVER_TIME_ACCOUNT_NAME,
  );

  const sectionsData: SectionedContentScreenModel = { sections: [] };

  const showFaqArticle = () => {
    showZendeskSection(ZendeskSections.VARO_PAY_OVER_TIME_FAQ);
  };

  const accountItems: VXLComponent[] = [
    {
      id: 'manage-account-statements-and-documents-list-item',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: translatedText('statementsAndDocuments'),
        secondaryCopy: '',
        analyticsId: 'statements-documents',
        onPress: () => {
          router.navigate({
            pathname: `/myvaro/statements-and-forms/account-statements/[id]`,
            params: {
              id: payOverTimeAccount?.id ?? accountId,
              category: 'payOverTime',
              account_type: 'payOverTime',
            },
          });
        },
      },
    },
  ];

  sectionsData.sections.push({
    sectionHeader: {
      id: 'manage-account-section-header',
      name: VXLComponentId.SECTION_DIVIDER,
      props: {
        labelText: translatedText('manageAccountHeader'),
      },
    },
    data: accountItems,
  });

  const helpItems: VXLComponent[] = [
    {
      id: 'payOverTime-faq-list-item',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: translatedText('payOverTimeFaqs'),
        secondaryCopy: '',
        analyticsId: 'payOverTimeFaqsClicked',
        customAttributes: {
          faq_name: 'pay_over_time',
        },
        onPress: showFaqArticle,
      },
    },
  ];

  sectionsData.sections.push({
    sectionHeader: {
      id: 'help-section-header',
      name: VXLComponentId.SECTION_DIVIDER,
      props: {
        labelText: translatedText('helpSectionHeader'),
      },
    },
    data: helpItems,
  });

  useBackHandler(router.back);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('screenTitle'),
          headerBackVisible: true,
          headerShown: true,
          gestureEnabled: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <SectionedContentScreen model={sectionsData} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default AccountSettings;
